/* ========================================================================
    Module: Annual conference form
 ========================================================================== */
import { AxiosResponse, AxiosError } from "axios";
import { appAxios } from "bootstrap";

import {
    RegularMemberSelectOptionOrganizationJsonType,
    CorporateSupportingMemberSelectOptionOrganizationJsonType,
} from "~types/organization-select-option";
import {
    regularMemberOrganizationSelectOptions,
    corporateSupportingMemberOrganizationSelectOptions,
} from "~modules/organization-select-option";

type OpinionExchangeMeetingPriceRules = Record<string, Record<string, number>>;

const annualConferenceForm = (): void => {
    const memberTypeElements =
        document.querySelectorAll<HTMLInputElement>(".js-member-type");

    if (memberTypeElements.length <= 0) {
        return;
    }

    let foundChecked = false;
    memberTypeElements.forEach((element) => {
        if (element.checked) {
            switchDisplayForm(element);

            foundChecked = true;
        }

        element.addEventListener("change", (e) => {
            if (!(e.currentTarget instanceof HTMLInputElement)) {
                return;
            }

            switchDisplayForm(e.currentTarget);
        });
    });

    if (!foundChecked) {
        // 全ての操作要素を非表示にして、内包するフォーム要素を無効化する
        itemVisibility("js-ac-visibility", false);
    }
};

const annualConferenceInvitationForm = (): void => {
    const memberTypeElements = document.querySelectorAll<HTMLInputElement>(
        ".js-invitation-member-type",
    );

    if (memberTypeElements.length <= 0) {
        return;
    }

    let foundChecked = false;
    memberTypeElements.forEach((element) => {
        if (element.checked) {
            switchInvitationDisplayForm(element);

            foundChecked = true;
        }

        element.addEventListener("change", (e) => {
            if (!(e.currentTarget instanceof HTMLInputElement)) {
                return;
            }

            switchInvitationDisplayForm(e.currentTarget);
        });
    });

    if (!foundChecked) {
        // 全ての操作要素を非表示にして、内包するフォーム要素を無効化する
        itemVisibility("js-ac-visibility", false);
    }
};

/**
 * 招待枠フォームの UNITT 役職で意見交換会の請求書宛名を切り替える
 */
const annualConferenceInvitationSpecialOffer = (): void => {
    const positionInUnitElements = document.querySelectorAll<HTMLInputElement>(
        ".js-position-in-unit",
    );

    positionInUnitElements.forEach((element) => {
        if (element.checked) {
            handleSpecialOfferToggle(element);
        }

        element.addEventListener("change", (e) => {
            if (!(e.currentTarget instanceof HTMLInputElement)) return;

            handleSpecialOfferToggle(e.currentTarget);
        });
    });
};

/**
 * 意見交換会の請求書宛名を切り替える
 *
 * @param {HTMLInputElement} element
 * @return {void}
 */
const handleSpecialOfferToggle = (element: HTMLInputElement): void => {
    const isSpecialOffer = element.dataset.isSpecialOffer === "1";

    const paidText = document.querySelector<HTMLElement>(
        ".js-opinion-exchange-meeting-paid",
    );
    const freeText = document.querySelector<HTMLElement>(
        ".js-opinion-exchange-meeting-free",
    );

    if (paidText && freeText) {
        paidText.style.display = isSpecialOffer ? "none" : "inline";
        freeText.style.display = isSpecialOffer ? "inline" : "none";
    }

    itemVisibility("js-opinion-exchange-meeting-invoice", !isSpecialOffer);
};

/**
 * 意見交換会の有料／無料設定で請求書宛名を切り替える
 */
const annualConferenceOpinionExchangeMeetingPrice = (): void => {
    const elements = document.querySelectorAll<HTMLInputElement>(
        ".js-participation-type, .js-position-in-unit",
    );

    if (elements.length <= 0) {
        return;
    }

    handleOpinionExchangeMeetingPriceToggle();

    elements.forEach((element) => {
        element.addEventListener("change", () => {
            handleOpinionExchangeMeetingPriceToggle();
        });
    });
};

/**
 * 意見交換会の有料／無料設定で請求書宛名を切り替える
 */
const handleOpinionExchangeMeetingPriceToggle = (): void => {
    const form = document.querySelector<HTMLFormElement>(
        "form[data-opinion-exchange-meeting-price-rules]",
    );

    const participationType = document.querySelector<HTMLInputElement>(
        ".js-participation-type:checked",
    );

    const positionInUnitt = document.querySelector<HTMLInputElement>(
        ".js-position-in-unit:checked",
    );

    if (!form || !participationType || !positionInUnitt) {
        itemVisibility("js-opinion-exchange-meeting-invoice", false);
        return;
    }

    const priceRules = JSON.parse(
        form.dataset.opinionExchangeMeetingPriceRules ?? "{}",
    ) as OpinionExchangeMeetingPriceRules;

    const paidType = Number(form.dataset.opinionExchangeMeetingPaidType ?? 0);

    const priceType =
        priceRules[participationType.value]?.[positionInUnitt.value];

    itemVisibility(
        "js-opinion-exchange-meeting-invoice",
        priceType === paidType,
    );
};

/**
 * フォームを切り替えて表示する
 *
 * @param {HTMLInputElement} element
 * @returns {void}
 */
const switchDisplayForm = (element: HTMLInputElement): void => {
    // 全ての操作要素を非表示にして、内包するフォーム要素を無効化する
    itemVisibility("js-ac-visibility", false);

    switch (element.value) {
        case "1":
            regularMember();
            break;
        case "2":
            corporateSupportingMember();
            break;
        case "3":
            individualSupportingMember();
            break;
        case "4":
            introduced();
            break;
        case "5":
            nonMember();
            break;
        case "6":
            student();
            break;
    }
};

/**
 * フォームを切り替えて表示する
 *
 * @param {HTMLInputElement} element
 * @returns {void}
 */
const switchInvitationDisplayForm = (element: HTMLInputElement): void => {
    // 全ての操作要素を非表示にして、内包するフォーム要素を無効化する
    itemVisibility("js-ac-visibility", false);

    const target = element.dataset.target;

    if (!target) {
        return;
    }

    switch (target) {
        case "regular-member":
            regularMember();
            break;
        case "corporate-supporting-member":
            corporateSupportingMember();
            break;
        case "individual-supporting-member":
            individualSupportingMember();
            break;
        case "introduced":
            introduced();
            break;
        case "non-member":
            nonMember();
            break;
        case "student":
            student();
            break;
    }
};

/**
 * 正会員のフォームを表示する
 *
 * @returns {void}
 */
const regularMember = (): void => {
    const organizationElement =
        document.querySelector<HTMLSelectElement>(".js-organization");

    if (!organizationElement) {
        return;
    }

    let oldValue: null | string = null;
    if (organizationElement.dataset.organizationNameSelectOld) {
        oldValue = organizationElement.dataset.organizationNameSelectOld;
    }

    itemVisibility("js-ac-regular-member", true);

    organizationElement.disabled = true;

    appAxios
        .get("/membership/api/members/regular-members/select-options")
        .then(
            (
                response: AxiosResponse<RegularMemberSelectOptionOrganizationJsonType>,
            ) => {
                const organizationOptions = response.data.data;

                let options =
                    '<option value="">組織・機関名をお選びください</option>';

                if (organizationOptions.length <= 0) {
                    organizationElement.innerHTML = options;
                    return;
                }

                // 組織・機関名のドロップダウンの選択項目を生成する
                options += regularMemberOrganizationSelectOptions(
                    organizationOptions,
                    oldValue,
                );

                organizationElement.innerHTML = options;
            },
        )
        .catch((errors: AxiosError) => {
            console.log(errors.message);
        })
        .finally(() => {
            organizationElement.disabled = false;
        });
};

/**
 * 賛助会員（法人）のフォームを表示する
 *
 * @returns {void}
 */
const corporateSupportingMember = (): void => {
    const organizationElement =
        document.querySelector<HTMLSelectElement>(".js-organization");

    if (!organizationElement) {
        return;
    }

    let oldValue: null | string = null;
    if (organizationElement.dataset.organizationNameSelectOld) {
        oldValue = organizationElement.dataset.organizationNameSelectOld;
    }

    itemVisibility("js-ac-corporate-support-member", true);

    organizationElement.disabled = true;

    appAxios
        .get(
            "/membership/api/members/corporate-supporting-members/select-options",
        )
        .then(
            (
                response: AxiosResponse<CorporateSupportingMemberSelectOptionOrganizationJsonType>,
            ) => {
                const organizationOptions = response.data.data;

                let options =
                    '<option value="">組織・機関名をお選びください</option>';

                if (organizationOptions.length <= 0) {
                    organizationElement.innerHTML = options;
                    return;
                }

                // 組織・機関名のドロップダウンの選択項目を生成する
                options += corporateSupportingMemberOrganizationSelectOptions(
                    organizationOptions,
                    oldValue,
                );

                organizationElement.innerHTML = options;
            },
        )
        .catch((errors: AxiosError) => {
            console.log(errors.message);
        })
        .finally(() => {
            organizationElement.disabled = false;
        });
};

/**
 * 賛助会員（個人）のフォームを表示する
 *
 * @returns {void}
 */
const individualSupportingMember = (): void => {
    itemVisibility("js-ac-individual-supporting-member", true);
};

/**
 * 正会員／賛助会員からの紹介（企業の方）のフォームを表示する
 *
 * @returns {void}
 */
const introduced = (): void => {
    itemVisibility("js-ac-introduced", true);
};

/**
 * 非会員のフォームを表示する
 *
 * @returns {void}
 */
const nonMember = (): void => {
    itemVisibility("js-ac-non-member", true);
};

/**
 * 学生のフォームを表示する
 *
 * @returns {void}
 */
const student = (): void => {
    itemVisibility("js-ac-student", true);
};

/**
 * 項目の表示／非表示を切り替えて、内包するフォーム要素を有効／無効化する
 *
 * @param {string} className
 * @param {boolean} isVisible
 * @returns {void}
 */
const itemVisibility = (className: string, isVisible: boolean): void => {
    const visibilityElements = document.querySelectorAll<HTMLElement>(
        `.${className}`,
    );

    if (visibilityElements.length > 0) {
        visibilityElements.forEach((element) => {
            if (isVisible) {
                if (element instanceof HTMLTableRowElement) {
                    element.style.display = "table-row";
                } else {
                    element.style.display = "block";
                }
            } else {
                element.style.display = "none";
            }

            const formControlsElements = element.querySelectorAll<
                | HTMLInputElement
                | HTMLSelectElement
                | HTMLTextAreaElement
                | HTMLButtonElement
            >("input, select, textarea, button");

            if (formControlsElements.length < 0) {
                return;
            }

            formControlsElements.forEach((element) => {
                element.disabled = !isVisible;
            });
        });
    }
};

export {
    annualConferenceForm,
    annualConferenceInvitationForm,
    annualConferenceInvitationSpecialOffer,
    annualConferenceOpinionExchangeMeetingPrice,
};
